import './ProductSizeSelection.scss';

import React, { useEffect, useMemo, useState } from 'react';
import {
  getSoldOutSizeTooltipMessage,
  resolveSizeStockDisplayInfo,
} from '../helpers/storeConfirmSizesStock.utils';
import { sortSizesForDisplay } from '../helpers/storeSizeConfig.utils';
import { STORE_CONFIRM_SIZES_LOCKED_SIZE_TOOLTIP } from '../../constants/storeCheckout.constants';
import type {
  ISizeSelectedState,
  IStocksData,
  IStoreOrderProduct,
} from '../helpers/storeConfirmSizes.types';
import { ToolTip } from '@/components/Tooltip/Tooltip';

const NO_DESIGN_IMAGE = '/src/assets/svg/proofs/no_design_new.svg';

interface IProductSizeSelectionProps {
  product: IStoreOrderProduct;
  sizes: ISizeSelectedState[];
  stockData?: IStocksData | null;
  isReadOnly?: boolean;
  onToggleSize: (sizeLabel: string) => void;
  onRemoveSize: (sizeLabel: string) => void;
}

const ProductSizeSelection: React.FC<IProductSizeSelectionProps> = ({
  product,
  sizes,
  stockData,
  isReadOnly = false,
  onToggleSize,
  onRemoveSize,
}) => {
  const [imageSrc, setImageSrc] = useState(product.imageUrl || NO_DESIGN_IMAGE);

  const sizesArray = useMemo(() => sortSizesForDisplay(sizes), [sizes]);

  useEffect(() => {
    setImageSrc(product.imageUrl || NO_DESIGN_IMAGE);
  }, [product.imageUrl]);

  const designIdLabel = product.alphaNumericOrderItemsId
    ? `#${product.alphaNumericOrderItemsId}`
    : '';

  const renderSizePillWithTooltip = (
    sizePill: React.ReactNode,
    isLockedByExistingOrder: boolean,
    soldOutTooltip?: string
  ) => {
    if (isLockedByExistingOrder) {
      return (
        <ToolTip
          title={STORE_CONFIRM_SIZES_LOCKED_SIZE_TOOLTIP}
          placement="top"
          getPopupContainer={() => document.body}
        >
          <span className="product-size-selection__tooltip-wrap">{sizePill}</span>
        </ToolTip>
      );
    }

    if (soldOutTooltip) {
      return (
        <ToolTip title={soldOutTooltip} placement="top" getPopupContainer={() => document.body}>
          <span className="product-size-selection__tooltip-wrap product-size-selection__tooltip-wrap--sold-out">
            {sizePill}
          </span>
        </ToolTip>
      );
    }

    return sizePill;
  };

  return (
    <div
      className={['product-size-selection', isReadOnly ? 'product-size-selection--readonly' : '']
        .filter(Boolean)
        .join(' ')}
      data-testid="product-size-selection"
    >
      <div className="product-size-selection__product">
        <div className="product-size-selection__image">
          <img
            src={imageSrc}
            alt={product.apparelName || designIdLabel}
            className="design-image"
            onError={() => setImageSrc(NO_DESIGN_IMAGE)}
          />
          {designIdLabel && <span className="design-id">{designIdLabel}</span>}
        </div>

        <div className="product-size-selection__content">
          <div className="product-size-selection__sizes">
            {sizesArray.map((size) => {
              const { warningMsg, warningColor, showWarning, isSoldOut } =
                resolveSizeStockDisplayInfo({
                  sizeLabel: size.label,
                  stockData,
                });
              const checkboxId = `size-checkbox-${product.orderItemId}-${size.label}`;
              const isLockedByExistingOrder = Boolean(size.lockedByExistingOrder);
              const isSizeSoldOutAndNotLocked = isSoldOut && !isLockedByExistingOrder;
              const isSizeInteractionDisabled =
                isReadOnly || isLockedByExistingOrder || size.disabled || isSizeSoldOutAndNotLocked;
              const isSizeSelected = isSizeSoldOutAndNotLocked ? false : size.selected;
              const soldOutTooltip = isSizeSoldOutAndNotLocked
                ? getSoldOutSizeTooltipMessage(size.label, stockData)
                : '';

              const sizePill = (
                <label
                  htmlFor={checkboxId}
                  className={[
                    'product-size-selection__input-container',
                    size.disabled ? 'size-disabled' : '',
                    isSizeSoldOutAndNotLocked ? 'size-sold-out' : '',
                    isReadOnly ? 'size-readonly' : '',
                    isLockedByExistingOrder ? 'size-locked-by-order' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    cursor: isSizeInteractionDisabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    id={checkboxId}
                    className="product-size-selection__toggle-checkbox standard-checked"
                    checked={isSizeSelected}
                    disabled={isSizeInteractionDisabled}
                    onChange={() => {
                      if (!isSizeInteractionDisabled) {
                        onToggleSize(size.label);
                      }
                    }}
                  />
                  <span className="size-label">{size.label}</span>

                  {size.addedByUser && !isReadOnly && !isLockedByExistingOrder && !size.disabled ? (
                    <button
                      type="button"
                      className="remove-icon"
                      aria-label={`Remove ${size.label}`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemoveSize(size.label);
                      }}
                    >
                      ×
                    </button>
                  ) : null}
                </label>
              );

              return (
                <div
                  key={`${product.orderItemId}-${size.label}`}
                  className="product-size-selection__size-container"
                >
                  {renderSizePillWithTooltip(sizePill, isLockedByExistingOrder, soldOutTooltip)}

                  {showWarning && (
                    <span
                      className="size-warning-msg"
                      style={{ color: warningColor }}
                      aria-live="polite"
                    >
                      {warningMsg}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSizeSelection;
